import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  FileSignature,
  Handshake,
  History as HistoryIcon,
  Loader2,
  LogOut,
  Menu,
  Search,
  Stamp,
  Table2,
  UsersRound,
  Wand2,
  X,
} from "lucide-react";
import { euipoApi } from "../../api/trademarkApi";
import { useAuth } from "../../app/AuthProvider";

/** Dot + label showing whether the EUIPO credentials currently authenticate. */
function ConnectionBadge() {
  const [state, setState] = useState({ status: "checking", environment: "" });
  const [verifying, setVerifying] = useState(false);

  const check = useCallback(async () => {
    try {
      const data = await euipoApi.status();
      setState({
        status: data.authorized ? "connected" : "disconnected",
        environment: data.environment,
        reason: data.reason,
      });
    } catch {
      setState({ status: "disconnected", environment: "" });
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  async function handleVerify() {
    setVerifying(true);
    try {
      const result = await euipoApi.verify();
      setState({ status: "connected", environment: result.environment });
    } catch (err) {
      setState({
        status: "disconnected",
        environment: state.environment,
        reason: err.message,
      });
    } finally {
      setVerifying(false);
    }
  }

  const connected = state.status === "connected";
  const label = verifying
    ? "Checking"
    : state.status === "checking"
      ? "Checking EUIPO"
      : connected
        ? `EUIPO ${state.environment}`
        : "EUIPO not connected";

  return (
    <button
      type="button"
      onClick={handleVerify}
      disabled={verifying}
      title={
        connected
          ? "Credentials verified. Click to re-check."
          : state.reason || "Click to verify your EUIPO API key and secret."
      }
      className="inline-flex w-full items-center gap-2 rounded border border-white/10 bg-white/5 px-3 py-2 text-micro font-medium text-ink-300 transition-colors hover:bg-white/10 disabled:opacity-60"
    >
      {verifying ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <span
          aria-hidden="true"
          className={`h-1.5 w-1.5 shrink-0 rounded-full ${
            connected
              ? "bg-live"
              : state.status === "checking"
                ? "bg-ink-400"
                : "bg-pending"
          }`}
        />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}

const NAV_ITEMS = [
  { to: "/", end: true, label: "Search", icon: Search },
  { to: "/results", label: "Results", icon: Table2 },
  { to: "/history", label: "History", icon: HistoryIcon },
  { to: "/logo-generator", label: "Generate Logo", icon: Wand2 },
  { to: "/nda", label: "NDA", icon: FileSignature },
  { to: "/potential-licensing", label: "Potential Licensing", icon: Handshake },
];

function initials(text) {
  const parts = (text || "")
    .trim()
    .split(/[\s@.]+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  return parts.length === 1
    ? parts[0].slice(0, 2).toUpperCase()
    : (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function AppSidebar() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleLogout() {
    setSigningOut(true);
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  const linkClass = ({ isActive }) =>
    `flex items-center gap-2.5 rounded px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "bg-white/10 text-white"
        : "text-ink-300 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <>
      {/* Mobile trigger — the sidebar is off-canvas below the lg breakpoint. */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        aria-label="Open menu"
        className="fixed left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded border border-ink-200 bg-white text-ink-700 shadow-raised lg:hidden"
      >
        <Menu className="h-4.5 w-4.5" aria-hidden="true" />
      </button>

      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-ink-950/50 backdrop-blur-[1px] lg:hidden"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 shrink-0 flex-col bg-ink-950 transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-14 items-center justify-between gap-2.5 border-b border-white/10 px-4">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-2.5"
            onClick={() => setMobileOpen(false)}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[linear-gradient(135deg,#8f5eeb,#7a54e5)]">
              <Stamp
                className="h-4 w-4 text-white"
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </span>
            <span className="truncate font-display text-[15px] font-semibold tracking-tight text-white">
              GetMyFirstTrademark
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-400 hover:bg-white/10 hover:text-white lg:hidden"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
          <div className="space-y-0.5">
            {NAV_ITEMS.map(({ to, end, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={linkClass}
                onClick={() => setMobileOpen(false)}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </div>

          {/* <ConnectionBadge /> */}
        </nav>

        {/* Admin area and account controls anchor to the bottom, separated
            by a rule, so they read as "account-level" rather than sitting
            among the everyday navigation above. */}
        <div className="border-t border-white/10 px-3 py-3">
          {isAdmin && (
            <NavLink
              to="/users"
              className={linkClass}
              onClick={() => setMobileOpen(false)}
            >
              <UsersRound className="h-4 w-4 shrink-0" aria-hidden="true" />
              User management
            </NavLink>
          )}

          <div className="mt-2 flex items-center gap-2.5 rounded px-2 py-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
              {initials(user?.name || user?.email)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-white">
                {user?.name || user?.email}
              </p>
              <p className="truncate text-micro capitalize text-ink-400">
                {user?.role}
              </p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={signingOut}
              aria-label="Sign out"
              title="Sign out"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-60"
            >
              {signingOut ? (
                <Loader2
                  className="h-3.5 w-3.5 animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
